/**
 * The two-minute read the Executive section exists for (round 10 A3).
 *
 * The section was a real nav item with a real promise -- "what the
 * board, the regulator and the sector see. Every figure here is derived
 * from the live estate and can be opened down to the finding behind
 * it." -- above four tiles that were the same four cards already on the
 * CRO's home page, pointing at the same four destinations. Nothing was
 * assembled there and nothing changed between visits.
 *
 * Five things, in the order a board meeting asks for them: the colour
 * and why, what has moved, what it is costing, what is waiting on the
 * reader, and the one number they will be asked about. Every figure is
 * composed server-side from the board snapshot, the resilience score
 * and the reader's own inbox, so it is the same arithmetic the signed
 * board PDF carries rather than a second opinion assembled here.
 *
 * WHAT IT WILL NOT SAY. Where the estate holds no earlier reading the
 * comparison reports an ABSENCE, not an improvement from zero; where
 * products carry no revenue the cost is marked a floor. Both are the
 * server's judgements, rendered rather than re-derived -- a client that
 * re-decided either could disagree with the PDF in the room.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import { ragPillTone, ragWordKey } from "shared/board/ragTone";
import { formatDate } from "shared/time/formatDate";
import { formatSAR } from "shared/money/money";

export interface ExecutiveMovedLine {
  key: string;
  /** NULL where the earlier date holds no reading at all. */
  before: number | null;
  after: number;
  higher_is_worse: boolean;
}

export interface ExecutiveWaitingItem {
  title: string;
  subtitle: string;
  deep_link: string;
  /** Round 16 I13: a composed title, as a key. */
  title_key?: string | null;
}

export interface ExecutiveBriefOut {
  as_of: string;
  compared_with: string;
  lookback_days: number;
  rag: string;
  rag_because: string;
  rag_because_key: string;
  rag_because_params: Record<string, string | number>;
  moved: ExecutiveMovedLine[];
  revenue_at_risk_annual: string;
  revenue_measured_count: number;
  revenue_unmeasured_count: number;
  waiting_total: number;
  waiting: ExecutiveWaitingItem[];
  score: number | null;
  /** Round 18 B17 -- null when nothing is scored. */
  score_tier: string | null;
  score_not_scored_reason?: string | null;
  score_confidence: number;
  score_confidence_capped: boolean;
  methodology_version: string;
  tolerance_breached: number | null;
  tolerance_assessed: number | null;
  mvb_breached: number | null;
  notes: string[];
  /** Round 16 I13: the notes as keys and parameters, index for index. */
  notes_said?: { key: string; params?: Record<string, unknown> }[];
}

export const executiveBriefKey = ["executive", "brief"] as const;

export function useExecutiveBrief(enabled: boolean) {
  return useQuery({
    queryKey: executiveBriefKey,
    queryFn: () => api.get<ExecutiveBriefOut>("/v1/executive/brief"),
    enabled,
    // A seat that cannot read it is refused once, not retried at it.
    retry: false,
  });
}

/* Round 12 B1 -- this mapped everything that was not red or amber to
   GREEN, so a fourth state would have headlined the board green over a
   bank that had assessed nothing. Shared, so the next surface inherits
   the decision rather than making it again. */
const toneFor = ragPillTone;

/**
 * A movement's tone, from the server's own statement of which
 * direction is bad. Re-deciding that here is how two surfaces end up
 * disagreeing about whether a number going up is good news.
 */
function movementTone(line: ExecutiveMovedLine): PillTone {
  if (line.before === null) return "neutral";
  const worse = line.after > line.before ? line.higher_is_worse : !line.higher_is_worse;
  return worse ? "amber" : "green";
}

function Block({
  title,
  testid,
  children,
}: {
  title: string;
  testid: string;
  children: React.ReactNode;
}) {
  return (
    <section data-testid={testid} className="space-y-1">
      {/* Round 12 H3 -- h2, not h3. The page's own heading is an h1 and
          these blocks sat at h3, so the ladder skipped a level on the
          one section built to be read in two minutes -- which is
          exactly the section somebody navigating by headings will jump
          into. The size is a class, not the level. */}
      <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {title}
      </h2>
      <div className="text-sm text-slate-800">{children}</div>
    </section>
  );
}

export function ExecutiveBrief({ enabled }: { enabled: boolean }): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const brief = useExecutiveBrief(enabled);

  if (!enabled) return null;
  if (brief.isLoading) {
    return (
      <p data-testid="executive-brief-loading" className="text-xs text-slate-500">
        {t("common.loading")}
      </p>
    );
  }
  // A read that failed is SAID. Rendering the tiles alone under the
  // section's promise is how it came to look like a shelf of links.
  if (brief.error || !brief.data) {
    return (
      <p data-testid="executive-brief-error" className="text-xs text-rose-700">
        {t("executive.brief.failed")}
      </p>
    );
  }
  const b = brief.data;

  return (
    <div
      data-testid="executive-brief"
      className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3"
    >
      <Block title={t("executive.brief.rag")} testid="executive-brief-rag">
        <StatusPill tone={toneFor(b.rag)} testid="executive-brief-rag-pill">
          {t(`executive.brief.ragWord.${ragWordKey(b.rag)}`)}
        </StatusPill>
        {/* The sentence is composed server-side as a KEY plus params so
            it renders in the reader's language, with the English the
            board PDF carries as the fallback -- the same handling the
            board view uses, deliberately, because the two must not
            drift into saying different things about one colour. */}
        <p className="mt-1 text-xs text-slate-600">
          {b.rag_because_key
            ? String(
                t(b.rag_because_key, {
                  ...b.rag_because_params,
                  rag: String(b.rag_because_params.rag ?? "").toUpperCase(),
                  severity: t(
                    `board.severity.${b.rag_because_params.severity ?? "any"}`,
                    { defaultValue: "" },
                  ),
                  defaultValue: b.rag_because,
                }),
              )
            : b.rag_because}
        </p>
        {b.tolerance_assessed !== null && (
          <p
            data-testid="executive-brief-tolerance"
            className="mt-1 text-xs text-slate-600"
          >
            {t("executive.brief.tolerance", {
              breached: b.tolerance_breached ?? 0,
              assessed: b.tolerance_assessed,
              mvb: b.mvb_breached ?? 0,
            })}
          </p>
        )}
      </Block>

      <Block title={t("executive.brief.moved")} testid="executive-brief-moved">
        <p className="text-xs text-slate-500">
          {t("executive.brief.since", {
            days: b.lookback_days,
            date: formatDate(b.compared_with, i18n.language),
          })}
        </p>
        {b.moved.length === 0 ? (
          <p
            data-testid="executive-brief-still"
            className="mt-1 text-xs text-slate-600"
          >
            {t("executive.brief.nothingMoved")}
          </p>
        ) : (
          <ul className="mt-1 space-y-1">
            {b.moved.map((line) => (
              <li
                key={line.key}
                data-testid="executive-brief-moved-line"
                data-key={line.key}
              >
                <StatusPill tone={movementTone(line)} mono>
                  {line.before === null
                    ? t("executive.brief.noEarlier", { after: line.after })
                    : `${line.before} → ${line.after}`}
                </StatusPill>
                {/* Round 11 F3 -- a separator in the text. `ms-2`
                    spaced the pill from the figure's name on screen
                    and joined them everywhere else: "NO EARLIER
                    READING, NOW 15business services". */}
                <span className="ms-2 text-xs text-slate-700">
                  <span aria-hidden="true"> · </span>
                  {t(`executive.brief.figure.${line.key}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
        {b.notes.map((n, i) => {
          // Round 16 I13 -- the note from the catalogue where the
          // server sent its key; its English where it did not.
          const said = b.notes_said?.[i];
          return (
            <p
              key={n}
              data-testid="executive-brief-note"
              className="mt-1 text-xs text-slate-500"
            >
              {said ? t(said.key, { ...(said.params ?? {}), defaultValue: n }) : n}
            </p>
          );
        })}
      </Block>

      <Block title={t("executive.brief.cost")} testid="executive-brief-cost">
        <p className="font-mono text-base text-slate-900">
          {formatSAR(b.revenue_at_risk_annual)}
        </p>
        <p className="mt-1 text-xs text-slate-600">
          {/* B1 -- "SAR 0 / Across 0 products" sat beside a GREEN
              headline on a bank with no products at all, and reads as a
              measured nothing rather than an unmeasured one. A5 dropped
              the vacuous "all of them with revenue recorded"; this is
              the other half. */}
          {b.revenue_measured_count === 0 && b.revenue_unmeasured_count === 0
            ? t("executive.brief.costNothingMeasured")
            : b.revenue_unmeasured_count > 0
              ? t("executive.brief.costFloor", {
                  measured: b.revenue_measured_count,
                  unmeasured: b.revenue_unmeasured_count,
                })
              : t("executive.brief.costComplete", {
                  measured: b.revenue_measured_count,
                })}
        </p>
      </Block>

      <Block title={t("executive.brief.waiting")} testid="executive-brief-waiting">
        {b.waiting_total === 0 ? (
          <p className="text-xs text-slate-600">{t("executive.brief.waitingNone")}</p>
        ) : (
          <>
            <p className="font-mono text-base text-slate-900">{b.waiting_total}</p>
            <ul className="mt-1 space-y-0.5">
              {b.waiting.map((w) => (
                <li key={w.deep_link + w.title}>
                  <Link
                    to={w.deep_link}
                    data-testid="executive-brief-waiting-item"
                    className="text-xs text-brand-brassDark underline decoration-dotted underline-offset-2"
                  >
                    {w.title_key ? t(w.title_key, { defaultValue: w.title }) : w.title}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/my-work"
              data-testid="executive-brief-waiting-all"
              className="mt-1 inline-block text-xs text-brand-brassDark underline decoration-dotted underline-offset-2"
            >
              {t("executive.brief.waitingAll")}
            </Link>
          </>
        )}
      </Block>

      <Block title={t("executive.brief.oneNumber")} testid="executive-brief-score">
        {b.score_not_scored_reason ? (
          /* Round 18 B17 -- it read "-- · Resilience Score tier E" over an
             estate with nothing admitted: a tier is a judgement, and
             nothing has been scored. */
          <p
            data-testid="executive-brief-not-scored"
            data-reason={b.score_not_scored_reason}
            className="text-sm text-slate-700"
          >
            {t("screens.resilienceScoreCard.notScored")}
          </p>
        ) : (
          <p className="font-mono text-base text-slate-900">
            {b.score === null ? "—" : b.score.toFixed(1)}
            {/* ... and "47.3C", which reads as one token and is two
              facts: a score out of a hundred and the band it falls in.
              A5: the string carried its own separator too, so the
              brief printed an empty middle token -- "40.7 · · tier C".
              One separator, here, where the spacing is decided. */}
            {" · "}
            <span className="ms-2 text-sm text-slate-600">
              {t("executive.brief.scoreTier", { tier: b.score_tier })}
            </span>
          </p>
        )}
        {/* The confidence of a score that was not computed is not a
            figure anybody can use; the walk read "57% confidence" under
            a tier over nothing. */}
        {!b.score_not_scored_reason && (
          <p className="mt-1 text-xs text-slate-600">
            {t("executive.brief.confidence", {
              pct: b.score_confidence.toFixed(0),
              version: b.methodology_version,
            })}
          </p>
        )}
        {!b.score_not_scored_reason && b.score_confidence_capped && (
          <p
            data-testid="executive-brief-capped"
            className="mt-1 text-xs text-amber-700"
          >
            {t("executive.brief.capped")}
          </p>
        )}
        <Link
          to="/readiness"
          className="mt-1 inline-block text-xs text-brand-brassDark underline decoration-dotted underline-offset-2"
        >
          {t("executive.brief.openScore")}
        </Link>
      </Block>
    </div>
  );
}
